import { Plus, X } from 'lucide-react';
import * as React from 'react';
import { accessoryKey, COMMON_ACCESSORIES } from '@cfl/assets-shared';

import { Button, Input, Label } from '@/components/cargofl-ui';
import { Hint } from '@/components/cargofl-ui';
import { cn } from '@/lib/utils';

/**
 * What goes with an asset: charger, bag, mouse.
 *
 * Toggle chips rather than a free-text box, because the list has to be COMPARABLE at
 * return time. Typed text gives "charger", "Charger" and "chrgr", and the missing-item
 * check then reports a charger missing that is sitting on the desk. The chips make the
 * common case one tap and the same string every time; the add box is there for the
 * stylus nobody predicted.
 *
 * Used in two places with the same shape - issuing (what went out) and returning
 * (what came back) - so the two lists are built identically and compare cleanly.
 */
export function AccessoryPicker({
  selected,
  onChange,
  /** At return: only what was actually issued is offered, so nobody can tick a bag
   *  back in that never went out. */
  options = COMMON_ACCESSORIES as readonly string[],
  label = 'Accessories included',
  hint,
  allowCustom = true,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
  options?: readonly string[];
  label?: string;
  hint?: string;
  allowCustom?: boolean;
}) {
  const [custom, setCustom] = React.useState('');

  const isOn = (name: string) => selected.some((s) => accessoryKey(s) === accessoryKey(name));

  const toggle = (name: string) => {
    onChange(
      isOn(name)
        ? selected.filter((s) => accessoryKey(s) !== accessoryKey(name))
        : [...selected, name],
    );
  };

  const addCustom = () => {
    const name = custom.trim();
    if (name === '' || isOn(name)) {
      setCustom('');
      return;
    }
    onChange([...selected, name]);
    setCustom('');
  };

  // Anything already selected that is not one of the offered chips - a custom entry,
  // or (at return) something handed back that was never issued. Shown so it can be
  // un-ticked; hiding it would make it unremovable.
  const extras = selected.filter((s) => !options.some((o) => accessoryKey(o) === accessoryKey(s)));

  return (
    <div className="space-y-2">
      <Label>{label}</Label>

      <div className="flex flex-wrap gap-1.5">
        {options.map((name) => (
          <Chip key={name} label={name} active={isOn(name)} onClick={() => toggle(name)} />
        ))}
        {extras.map((name) => (
          <Chip key={name} label={name} active onClick={() => toggle(name)} custom />
        ))}
      </div>

      {allowCustom && (
        <div className="flex gap-2">
          <Input
            className="h-8 max-w-[220px] text-sm"
            placeholder="Something else…"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Otherwise Enter submits the surrounding form and issues the asset
                // with a half-typed accessory still in the box.
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <Button hint={"Add this accessory to the list"} type="button" size="sm" variant="secondary" onClick={addCustom} className="h-8">
            <Plus className="mr-1 size-3.5" /> Add
          </Button>
        </div>
      )}

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {hint ??
          'Small items that travel with the asset and are never stickered. Anything valuable enough to need its own history — a docking station, a second monitor — should be registered as its own asset instead.'}
      </p>
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
  custom,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  custom?: boolean;
}) {
  return (
    <Hint label={'Include or remove this accessory'}><button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'border-primary bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
      {active && custom && <X className="size-3 opacity-60" />}
    </button></Hint>
  );
}
